/**
 * SectionCard — Card de seção padronizado
 *
 * Wrapper com título, link opcional e children.
 * Substitui os <Box bgcolor border borderRadius p> manuais no Dashboard.
 *
 * Uso:
 *   <SectionCard title="Visão Geral" linkLabel="Ver todas →" onLink={() => navigate('/playlists')}>
 *     {conteúdo}
 *   </SectionCard>
 */

import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import { ReactNode } from 'react'
import { ds } from '../../theme/ds'

type Props = {
  title: string
  linkLabel?: string
  onLink?: () => void
  children: ReactNode
  /** Altura mínima do card — útil para cards de mapa/gráfico */
  minHeight?: number | string
}

export default function SectionCard({ title, linkLabel, onLink, children, minHeight }: Props) {
  const theme    = useTheme()
  const isDark   = theme.palette.mode === 'dark'
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const bg  = isDark ? '#152028' : '#ffffff'
  const bdr = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'

  return (
    <Box sx={{
      bgcolor: bg,
      border: `1px solid ${bdr}`,
      borderRadius: `${ds.radius.card}px`,
      p: isMobile ? ds.spacing.cardPaddingMobile : ds.spacing.cardPaddingDesktop,
      boxShadow: isDark ? ds.shadow.cardDark : ds.shadow.card,
      minHeight,
      display: 'flex',
      flexDirection: 'column',
    }}>

      {/* Cabeçalho do card */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 1.75,
        flexShrink: 0,
      }}>
        <Typography sx={{
          fontSize: ds.font.cardTitle,
          fontWeight: ds.font.semibold,
          color: 'text.primary',
        }}>
          {title}
        </Typography>

        {linkLabel && onLink && (
          <Typography
            variant="caption"
            onClick={onLink}
            sx={{
              color: 'text.secondary',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              userSelect: 'none',
              transition: `color ${ds.motion.fast}`,
              '&:hover': { color: 'text.primary' },
            }}
          >
            {linkLabel}
          </Typography>
        )}
      </Box>

      {/* Conteúdo */}
      <Box sx={{ flex: 1 }}>
        {children}
      </Box>
    </Box>
  )
}
